import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiUsers, FiPlus, FiEdit2, FiTrash2, FiX, FiSliders, FiCheckSquare } from 'react-icons/fi';
import { normalizeDepartment, getDefaultActivitiesForDepartment } from '../utils/departmentMapping';

const STAFF_DISPATCH_ACTIVITIES = [
  'Housekeeping Service',
  'Maintenance Assistance',
  'IT Support',
  'Front Office',
  'Medical Emergency',
  'Security Assistance',
  'SPA Appointment',
  'Yoga & Wellness',
  'Salon'
];

const DEPT_COLORS = {
  'Housekeeping Service':     { bg: 'bg-blue-50',   ring: 'ring-blue-200',   dot: 'bg-blue-500',   text: 'text-blue-700' },
  'Maintenance Assistance':   { bg: 'bg-orange-50',  ring: 'ring-orange-200',  dot: 'bg-orange-500',  text: 'text-orange-700' },
  'IT Support':               { bg: 'bg-purple-50',  ring: 'ring-purple-200',  dot: 'bg-purple-500',  text: 'text-purple-700' },
  'Front Office':             { bg: 'bg-teal-50',    ring: 'ring-teal-200',    dot: 'bg-teal-500',    text: 'text-teal-700' },
  'Security Assistance':      { bg: 'bg-rose-50',    ring: 'ring-rose-200',    dot: 'bg-rose-500',    text: 'text-rose-700' },
  'SPA Appointment':          { bg: 'bg-pink-50',    ring: 'ring-pink-200',    dot: 'bg-pink-500',    text: 'text-pink-700' },
  'Yoga & Wellness':          { bg: 'bg-green-50',   ring: 'ring-green-200',   dot: 'bg-green-500',   text: 'text-green-700' },
  'Salon':                    { bg: 'bg-fuchsia-50', ring: 'ring-fuchsia-200', dot: 'bg-fuchsia-500', text: 'text-fuchsia-700' }
};

const DEFAULT_COLOR = { bg: 'bg-gray-50', ring: 'ring-gray-200', dot: 'bg-gray-400', text: 'text-gray-700' };

const ROOM_PRESETS = [
  { label: 'A Wing', type: 'range', start: '101', end: '120' },
  { label: 'B Wing', type: 'range', start: '201', end: '220' },
  { label: 'C Wing', type: 'range', start: '301', end: '320' },
  { label: 'All Rooms', type: 'range', start: '1', end: '9999' },
];

const emptyForm = (department) => {
  return {
    department,
    name: '',
    phone: '',
    is_hod: false,
    is_hidden: false
  };
};

function DepartmentStaff() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sectionParam = new URLSearchParams(location.search).get('section');
  const initialSection = sectionParam === 'routing'
    ? 'routing'
    : sectionParam === 'defaults' || sectionParam === 'escalation'
      ? 'defaults'
      : 'staff';
  const [activeSection, setActiveSection] = useState(initialSection);
  
  const displayActivities = useMemo(() => {
    if (user?.role === 'department' && user?.department) {
      const normalizedUserDepartment = normalizeDepartment(user.department);
      const defaults = getDefaultActivitiesForDepartment(normalizedUserDepartment);
      if (defaults.length > 0) {
        return defaults.filter((activity) => STAFF_DISPATCH_ACTIVITIES.includes(activity));
      }
      const directMatch = STAFF_DISPATCH_ACTIVITIES.filter(
        (d) => d.toLowerCase() === String(normalizedUserDepartment || '').toLowerCase()
      );
      return directMatch.length > 0 ? directMatch : [STAFF_DISPATCH_ACTIVITIES[0]];
    }
    return STAFF_DISPATCH_ACTIVITIES;
  }, [user]);

  const [departmentStaff, setDepartmentStaff] = useState([]);
  const [staffForm, setStaffForm] = useState(() => emptyForm(STAFF_DISPATCH_ACTIVITIES[0]));
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffSubmitting, setStaffSubmitting] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState('');
  const [staffEditForm, setStaffEditForm] = useState(() => emptyForm(STAFF_DISPATCH_ACTIVITIES[0]));
  const [staffEditSubmitting, setStaffEditSubmitting] = useState(false);
  const [routingRules, setRoutingRules] = useState([]);
  const [routingMessage, setRoutingMessage] = useState('');
  const [defaultsMessage, setDefaultsMessage] = useState('');
  const [loadingRouting, setLoadingRouting] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState('');
  const [routingEditSubmitting, setRoutingEditSubmitting] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [routingDefaultsForm, setRoutingDefaultsForm] = useState({
    department: STAFF_DISPATCH_ACTIVITIES[0],
    escalation_minutes: 10,
    guest_followup_ack_minutes: '',
    hod_staff_ids: []
  });
  const [routingDefaultsMap, setRoutingDefaultsMap] = useState({});
  const [newRule, setNewRule] = useState({
    department: STAFF_DISPATCH_ACTIVITIES[0],
    room_match_type: 'range',
    room_start: '1',
    room_end: '9999',
    room_custom_values: '',
    primary_phones: [],
    escalation_phones: [],
    super_hod_phones: [],
    super_hod_escalation_minutes: 0
  });
  const [editRuleForm, setEditRuleForm] = useState({
    department: STAFF_DISPATCH_ACTIVITIES[0],
    room_match_type: 'range',
    room_start: '1',
    room_end: '9999',
    room_custom_values: '',
    primary_phones: [],
    escalation_phones: [],
    super_hod_phones: [],
    super_hod_escalation_minutes: 0
  });
  const [fullFlowSettings, setFullFlowSettings] = useState({});
  const [roomPresets, setRoomPresets] = useState([]);
  const [showPresetManager, setShowPresetManager] = useState(false);
  const [newPreset, setNewPreset] = useState({ label: '', type: 'range', start: '', end: '', custom: '' });
  const [createStep, setCreateStep] = useState(1);
  const [editStep, setEditStep] = useState(1);
  const formRef = useRef(null);

  useEffect(() => {
    const querySection = new URLSearchParams(location.search).get('section');
    const section = querySection === 'routing'
      ? 'routing'
      : querySection === 'defaults' || querySection === 'escalation'
        ? 'defaults'
        : 'staff';
    setActiveSection(section);
  }, [location.search]);

  const resetForm = useCallback(
    (department = STAFF_DISPATCH_ACTIVITIES[0]) => {
      setStaffForm(emptyForm(department));
      setEditingStaffId('');
    },
    []
  );

  const fetchDepartmentStaff = useCallback(async () => {
    try {
      setStaffLoading(true);
      const res = await api.get('/api/admin/staff');
      setDepartmentStaff(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDepartmentStaff([]);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const fetchRoutingRules = useCallback(async () => {
    try {
      setLoadingRouting(true);
      const [routingRes, settingsRes] = await Promise.allSettled([
        api.get('/api/admin/staff-routing-rules'),
        api.get('/api/admin/whatsapp-flow-settings')
      ]);
      if (routingRes.status === 'fulfilled') {
        setRoutingRules(Array.isArray(routingRes.value?.data) ? routingRes.value.data : []);
      } else {
        setRoutingRules([]);
      }
      if (settingsRes.status === 'fulfilled') {
        setFullFlowSettings(settingsRes.value?.data || {});
        setRoomPresets(Array.isArray(settingsRes.value?.data?.room_presets) ? settingsRes.value.data.room_presets : []);
      } else {
        setRoomPresets([]);
      }
    } catch {
      setRoutingRules([]);
      setRoomPresets([]);
      setRoutingMessage('Failed to load dispatch routing rules');
    } finally {
      setLoadingRouting(false);
    }
  }, []);

  const fetchRoutingDefaults = useCallback(async () => {
    try {
      const response = await api.get('/api/admin/staff-routing-defaults');
      const data = response.data && typeof response.data === 'object' ? response.data : {};
      setRoutingDefaultsMap(data);
    } catch {
      setRoutingDefaultsMap({});
    }
  }, []);

  useEffect(() => {
    fetchDepartmentStaff();
    fetchRoutingRules();
    fetchRoutingDefaults();
  }, [fetchDepartmentStaff, fetchRoutingRules, fetchRoutingDefaults]);

  useEffect(() => {
    const defaultDepartment = displayActivities[0] || STAFF_DISPATCH_ACTIVITIES[0];
    setStaffForm((prev) => (displayActivities.includes(prev.department) ? prev : { ...prev, department: defaultDepartment }));
    setNewRule((prev) => (displayActivities.includes(prev.department) ? prev : { ...prev, department: defaultDepartment }));
    setRoutingDefaultsForm((prev) => (displayActivities.includes(prev.department) ? prev : { ...prev, department: defaultDepartment }));
  }, [displayActivities]);

  useEffect(() => {
    const department = routingDefaultsForm.department;
    const defaults = routingDefaultsMap?.[department];
    if (!defaults) {
      setRoutingDefaultsForm((prev) => ({
        ...prev,
        escalation_minutes: 10,
        guest_followup_ack_minutes: '',
        hod_staff_ids: []
      }));
      return;
    }

    const hodOptions = (departmentStaff || []).filter(
      (staff) => staff.department === department && !!staff.is_hod && staff.is_active
    );
    const hodIdSet = new Set(hodOptions.map((s) => String(s.id)));

    // Support both new hod_staff_ids[] and legacy hod_staff_id
    const rawIds = Array.isArray(defaults.hod_staff_ids) && defaults.hod_staff_ids.length > 0
      ? defaults.hod_staff_ids
      : (defaults.hod_staff_id ? [defaults.hod_staff_id] : []);
    const hod_staff_ids = rawIds.map((id) => String(id)).filter((id) => hodIdSet.has(id));

    setRoutingDefaultsForm((prev) => ({
      ...prev,
      escalation_minutes: defaults.escalation_minutes ?? 10,
      guest_followup_ack_minutes:
        defaults.guest_followup_ack_minutes === null || defaults.guest_followup_ack_minutes === undefined
          ? ''
          : defaults.guest_followup_ack_minutes,
      hod_staff_ids
    }));
  }, [routingDefaultsForm.department, routingDefaultsMap, departmentStaff]);

  const handleAddDepartmentStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.department || !staffForm.name.trim() || !staffForm.phone.trim()) return;
    try {
      setStaffSubmitting(true);
      const payload = {
        department: staffForm.department,
        name: staffForm.name.trim(),
        phone: staffForm.phone.trim(),
        is_hod: !!staffForm.is_hod,
        is_hidden: !!staffForm.is_hidden
      };
      await api.post('/api/admin/staff', payload);
      resetForm(staffForm.department);
      fetchDepartmentStaff();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save staff');
    } finally {
      setStaffSubmitting(false);
    }
  };

  const handleDeleteDepartmentStaff = async (staffId) => {
    if (!window.confirm('Remove this staff member?')) return;
    try {
      await api.delete(`/api/admin/staff/${staffId}`);
      fetchDepartmentStaff();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete staff');
    }
  };

  const handleEditDepartmentStaff = (staff) => {
    setEditingStaffId(staff.id);
    setStaffEditForm({
      department: staff.department,
      name: staff.name || '',
      phone: staff.phone || '',
      is_hod: !!staff.is_hod,
      is_hidden: !!staff.is_hidden
    });
  };

  const closeStaffEditModal = () => {
    setEditingStaffId('');
    setStaffEditForm(emptyForm(displayActivities[0] || STAFF_DISPATCH_ACTIVITIES[0]));
  };

  const handleUpdateDepartmentStaff = async (e) => {
    e.preventDefault();
    if (!editingStaffId || !staffEditForm.department || !staffEditForm.name.trim() || !staffEditForm.phone.trim()) return;
    try {
      setStaffEditSubmitting(true);
      await api.put(`/api/admin/staff/${editingStaffId}`, {
        department: staffEditForm.department,
        name: staffEditForm.name.trim(),
        phone: staffEditForm.phone.trim(),
        is_hod: !!staffEditForm.is_hod,
        is_hidden: !!staffEditForm.is_hidden
      });
      closeStaffEditModal();
      fetchDepartmentStaff();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update staff');
    } finally {
      setStaffEditSubmitting(false);
    }
  };

  // Quick-add: pre-fill department and scroll to form
  const handleQuickAdd = (department) => {
    resetForm(department);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const staffByDepartment = useMemo(
    () =>
      displayActivities.reduce((acc, dept) => {
        acc[dept] = departmentStaff.filter((s) => s.department === dept);
        return acc;
      }, {}),
    [departmentStaff]
  );

  const selectedDeptColor = DEPT_COLORS[staffForm.department] || DEFAULT_COLOR;
  const normalizePhoneForMatch = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const getEmployeeOptions = (department) => {
    return (departmentStaff || []).filter((staff) => staff.department === department && !staff.is_hod && staff.is_active);
  };

  const getHodOptions = (department) => {
    return (departmentStaff || []).filter((staff) => staff.department === department && !!staff.is_hod && staff.is_active);
  };

  const findStaffIdByPhone = (phone, isHod) => {
    const target = normalizePhoneForMatch(phone);
    if (!target) return '';
    const match = (departmentStaff || []).find((staff) => {
      const p = normalizePhoneForMatch(staff.phone);
      if (!p || p !== target) return false;
      return isHod ? !!staff.is_hod : !staff.is_hod;
    });
    return match?.id || '';
  };

  const findStaffIdsByPhones = (phones = []) => {
    const targets = Array.from(new Set((Array.isArray(phones) ? phones : [phones]).map((phone) => normalizePhoneForMatch(phone)).filter(Boolean)));
    if (targets.length === 0) return [];
    return (departmentStaff || [])
      .filter((staff) => {
        const p = normalizePhoneForMatch(staff.phone);
        return p && targets.includes(p) && !staff.is_hod;
      })
      .map((staff) => String(staff.id));
  };

  const savePresets = async (presets) => {
    setRoomPresets(presets);
    try {
      const updatedSettings = { ...fullFlowSettings, room_presets: presets };
      await api.put('/api/admin/whatsapp-flow-settings', updatedSettings);
      setFullFlowSettings(updatedSettings);
    } catch (e) {
      console.error('Failed to save room presets', e);
    }
  };

  const addPreset = () => {
    if (!newPreset.label.trim()) return;
    const updated = [...roomPresets, { ...newPreset, label: newPreset.label.trim() }];
    savePresets(updated);
    setNewPreset({ label: '', type: 'range', start: '', end: '', custom: '' });
  };

  const removePreset = (idx) => {
    savePresets(roomPresets.filter((_, i) => i !== idx));
  };

  const getPresetString = (preset) => preset.type === 'range' ? `${preset.start}-${preset.end}` : preset.custom;

  const isPresetActive = (ruleForm, preset) => {
    const pStr = getPresetString(preset);
    const presetTokens = pStr.split(',').map(s => s.trim()).filter(Boolean);
    
    if (ruleForm.room_match_type === 'range') {
      const rStr = `${ruleForm.room_start}-${ruleForm.room_end}`;
      return presetTokens.length > 0 && presetTokens.every(t => t === rStr);
    }
    const currentStrs = (ruleForm.room_custom_values || '').split(',').map(s => s.trim()).filter(Boolean);
    return presetTokens.length > 0 && presetTokens.every(t => currentStrs.includes(t));
  };

  const togglePreset = (preset, ruleForm, setRuleForm) => {
    const pStr = getPresetString(preset);
    const presetTokens = pStr.split(',').map(s => s.trim()).filter(Boolean);
    const currentlyActive = isPresetActive(ruleForm, preset);
    
    let currentStrs = [];
    if (ruleForm.room_match_type === 'range') {
        const rStr = `${ruleForm.room_start}-${ruleForm.room_end}`;
        if (ruleForm.room_start && ruleForm.room_end && rStr !== '1-9999') {
            currentStrs.push(rStr);
        }
    } else {
        currentStrs = (ruleForm.room_custom_values || '').split(',').map(s => s.trim()).filter(Boolean);
    }
    
    if (currentlyActive) {
        currentStrs = currentStrs.filter(t => !presetTokens.includes(t));
    } else {
        for (const t of presetTokens) {
            if (!currentStrs.includes(t)) currentStrs.push(t);
        }
    }
    
    if (currentStrs.length === 0) {
        setRuleForm(prev => ({ ...prev, room_match_type: 'range', room_start: '', room_end: '', room_custom_values: '' }));
        return;
    }
    
    if (currentStrs.length === 1 && currentStrs[0].includes('-')) {
        const [s, e] = currentStrs[0].split('-');
        setRuleForm(prev => ({ ...prev, room_match_type: 'range', room_start: s, room_end: e, room_custom_values: '' }));
        return;
    }
    
    setRuleForm(prev => ({ ...prev, room_match_type: 'custom', room_start: '', room_end: '', room_custom_values: currentStrs.join(', ') }));
  };

  const getRoomMatchLabel = (rule) => {
    const ruleStr = rule.room_match_type === 'custom' 
      ? (rule.room_custom_values || '') 
      : `${rule.room_start}-${rule.room_end}`;
      
    if (!roomPresets || roomPresets.length === 0) {
      return rule.room_match_type === 'custom' ? (rule.room_custom_values || 'Custom') : `${rule.room_start} - ${rule.room_end}`;
    }

    let remainingTokens = ruleStr.split(',').map(s => s.trim()).filter(Boolean);
    const matchedPresetLabels = [];

    // Sort presets by length of tokens (descending) so we match larger presets first
    const sortedPresets = [...roomPresets].sort((a, b) => {
      const aLen = getPresetString(a).split(',').filter(Boolean).length;
      const bLen = getPresetString(b).split(',').filter(Boolean).length;
      return bLen - aLen;
    });

    for (const preset of sortedPresets) {
      const pTokens = getPresetString(preset).split(',').map(s => s.trim()).filter(Boolean);
      if (pTokens.length === 0) continue;

      // Check if ALL tokens of this preset are in remainingTokens
      const isMatch = pTokens.every(pt => remainingTokens.includes(pt));
      if (isMatch) {
        matchedPresetLabels.push(preset.label);
        // Remove these tokens from remainingTokens
        remainingTokens = remainingTokens.filter(rt => !pTokens.includes(rt));
      }
    }

    // Combine matched preset labels and any leftover individual room numbers/ranges
    const allLabels = [...matchedPresetLabels, ...remainingTokens];
    return allLabels.length > 0 ? allLabels.join(', ') : 'Custom';
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    navigate(`/department-staff?section=${section}`, { replace: true });
  };

  const normalizePhone = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const togglePrimaryStaffSelection = (staffPhone) => {
    const target = normalizePhone(staffPhone);
    if (!target) return;
    setNewRule((prev) => {
      const selected = (prev.primary_phones || []).map(normalizePhone).filter(Boolean);
      const exists = selected.includes(target);
      return {
        ...prev,
        primary_phones: exists
          ? (prev.primary_phones || []).filter((p) => normalizePhone(p) !== target)
          : [...(prev.primary_phones || []), staffPhone]
      };
    });
  };

  const toggleEscalationStaffSelection = (staffPhone) => {
    const target = normalizePhone(staffPhone);
    if (!target) return;
    setNewRule((prev) => {
      const selected = (prev.escalation_phones || []).map(normalizePhone).filter(Boolean);
      const exists = selected.includes(target);
      return {
        ...prev,
        escalation_phones: exists
          ? (prev.escalation_phones || []).filter((p) => normalizePhone(p) !== target)
          : [...(prev.escalation_phones || []), staffPhone]
      };
    });
  };

  const toggleSuperHodStaffSelection = (staffPhone) => {
    const target = normalizePhone(staffPhone);
    if (!target) return;
    setNewRule((prev) => {
      const selected = (prev.super_hod_phones || []).map(normalizePhone).filter(Boolean);
      const exists = selected.includes(target);
      return {
        ...prev,
        super_hod_phones: exists
          ? (prev.super_hod_phones || []).filter((p) => normalizePhone(p) !== target)
          : [...(prev.super_hod_phones || []), staffPhone]
      };
    });
  };

  const toggleEditPrimaryStaffSelection = (staffPhone) => {
    const target = normalizePhone(staffPhone);
    if (!target) return;
    setEditRuleForm((prev) => {
      const selected = (prev.primary_phones || []).map(normalizePhone).filter(Boolean);
      const exists = selected.includes(target);
      return {
        ...prev,
        primary_phones: exists
          ? (prev.primary_phones || []).filter((p) => normalizePhone(p) !== target)
          : [...(prev.primary_phones || []), staffPhone]
      };
    });
  };

  const toggleEditEscalationStaffSelection = (staffPhone) => {
    const target = normalizePhone(staffPhone);
    if (!target) return;
    setEditRuleForm((prev) => {
      const selected = (prev.escalation_phones || []).map(normalizePhone).filter(Boolean);
      const exists = selected.includes(target);
      return {
        ...prev,
        escalation_phones: exists
          ? (prev.escalation_phones || []).filter((p) => normalizePhone(p) !== target)
          : [...(prev.escalation_phones || []), staffPhone]
      };
    });
  };

  const toggleEditSuperHodStaffSelection = (staffPhone) => {
    const target = normalizePhone(staffPhone);
    if (!target) return;
    setEditRuleForm((prev) => {
      const selected = (prev.super_hod_phones || []).map(normalizePhone).filter(Boolean);
      const exists = selected.includes(target);
      return {
        ...prev,
        super_hod_phones: exists
          ? (prev.super_hod_phones || []).filter((p) => normalizePhone(p) !== target)
          : [...(prev.super_hod_phones || []), staffPhone]
      };
    });
  };

  const cancelEditRule = () => {
    setEditingRuleId('');
    setEditStep(1);
    setEditRuleForm({
      department: displayActivities[0] || STAFF_DISPATCH_ACTIVITIES[0],
      room_match_type: 'range',
      room_start: '1',
      room_end: '9999',
      room_custom_values: '',
      primary_phones: [],
      escalation_phones: [],
      super_hod_phones: [],
      super_hod_escalation_minutes: 0
    });
    setRoutingMessage('');
  };

  const startEditRule = (rule) => {
    const existingPrimaryPhones = Array.isArray(rule.primary_phones) && rule.primary_phones.length > 0
      ? rule.primary_phones
      : [rule.primary_phone].filter(Boolean);
    const existingEscalationPhones = Array.isArray(rule.escalation_phones) && rule.escalation_phones.length > 0
      ? rule.escalation_phones
      : [rule.escalation_phone].filter(Boolean);
    const existingSuperHodPhones = Array.isArray(rule.super_hod_phones) && rule.super_hod_phones.length > 0
      ? rule.super_hod_phones
      : [rule.super_hod_phone].filter(Boolean);
    setEditingRuleId(rule.id);
    setEditStep(1);
    setEditRuleForm({
      department: rule.department,
      room_match_type: rule.room_match_type || 'range',
      room_start: String(rule.room_start ?? '1'),
      room_end: String(rule.room_end ?? '9999'),
      room_custom_values: rule.room_custom_values || '',
      primary_phones: existingPrimaryPhones,
      escalation_phones: existingEscalationPhones,
      super_hod_phones: existingSuperHodPhones,
      super_hod_escalation_minutes: rule.super_hod_escalation_minutes || 0
    });
  };

  const saveRoutingRule = async (e) => {
    e.preventDefault();
    setRoutingMessage('');
    const selectedPrimaryPhones = Array.from(
      new Set((newRule.primary_phones || []).map(normalizePhone).filter(Boolean))
    );
    if (selectedPrimaryPhones.length === 0) {
      setRoutingMessage('Please select at least one employee');
      return;
    }
    const selectedEscalationPhones = Array.from(
      new Set((newRule.escalation_phones || []).map(normalizePhone).filter(Boolean))
    );
    const selectedSuperHodPhones = Array.from(
      new Set((newRule.super_hod_phones || []).map(normalizePhone).filter(Boolean))
    );
    const payload = {
      department: newRule.department,
      room_match_type: newRule.room_match_type,
      room_start: parseInt(newRule.room_start, 10),
      room_end: parseInt(newRule.room_end, 10),
      room_custom_values: newRule.room_custom_values || null,
      primary_phones: selectedPrimaryPhones,
      primary_phone: selectedPrimaryPhones[0],
      escalation_phones: selectedEscalationPhones,
      escalation_phone: selectedEscalationPhones[0] || null,
      super_hod_phones: selectedSuperHodPhones,
      super_hod_phone: selectedSuperHodPhones[0] || null,
      super_hod_escalation_minutes: parseInt(newRule.super_hod_escalation_minutes, 10) || 0,
      is_active: true
    };

    if (newRule.room_match_type === 'custom') {
      payload.room_start = 0;
      payload.room_end = 0;
      payload.room_custom_values = String(newRule.room_custom_values || '').trim();
      if (!payload.room_custom_values) {
        setRoutingMessage('Please provide custom room values');
        return;
      }
    } else {
      if (Number.isNaN(payload.room_start) || Number.isNaN(payload.room_end) || payload.room_start > payload.room_end) {
        setRoutingMessage('Please provide a valid room range');
        return;
      }
    }

    try {
      await api.post('/api/admin/staff-routing-rules', payload);
      setRoutingMessage('Routing rule created successfully');
      setCreateStep(1);
      setNewRule({
        department: displayActivities[0] || STAFF_DISPATCH_ACTIVITIES[0],
        room_match_type: 'range',
        room_start: '1',
        room_end: '9999',
        room_custom_values: '',
        primary_phones: [],
        escalation_phones: [],
        super_hod_phones: [],
        super_hod_escalation_minutes: 0
      });
      fetchRoutingRules();
      fetchRoutingDefaults();
    } catch (error) {
      setRoutingMessage(error.response?.data?.error || 'Failed to save routing rule');
    }
  };

  const handleUpdateRoutingRule = async (e) => {
    e.preventDefault();
    if (!editingRuleId) return;
    setRoutingMessage('');
    const selectedPrimaryPhones = Array.from(
      new Set((editRuleForm.primary_phones || []).map(normalizePhone).filter(Boolean))
    );
    if (selectedPrimaryPhones.length === 0) {
      setRoutingMessage('Please select at least one employee');
      return;
    }
    const selectedEscalationPhones = Array.from(
      new Set((editRuleForm.escalation_phones || []).map(normalizePhone).filter(Boolean))
    );
    const selectedSuperHodPhones = Array.from(
      new Set((editRuleForm.super_hod_phones || []).map(normalizePhone).filter(Boolean))
    );
    const payload = {
      department: editRuleForm.department,
      room_match_type: editRuleForm.room_match_type,
      room_start: parseInt(editRuleForm.room_start, 10),
      room_end: parseInt(editRuleForm.room_end, 10),
      room_custom_values: editRuleForm.room_custom_values || null,
      primary_phones: selectedPrimaryPhones,
      primary_phone: selectedPrimaryPhones[0],
      escalation_phones: selectedEscalationPhones,
      escalation_phone: selectedEscalationPhones[0] || null,
      super_hod_phones: selectedSuperHodPhones,
      super_hod_phone: selectedSuperHodPhones[0] || null,
      super_hod_escalation_minutes: parseInt(editRuleForm.super_hod_escalation_minutes, 10) || 0,
      is_active: true
    };

    if (editRuleForm.room_match_type === 'custom') {
      payload.room_start = 0;
      payload.room_end = 0;
      payload.room_custom_values = String(editRuleForm.room_custom_values || '').trim();
      if (!payload.room_custom_values) {
        setRoutingMessage('Please provide custom room values');
        return;
      }
    } else if (Number.isNaN(payload.room_start) || Number.isNaN(payload.room_end) || payload.room_start > payload.room_end) {
      setRoutingMessage('Please provide a valid room range');
      return;
    }

    try {
      setRoutingEditSubmitting(true);
      await api.put(`/api/admin/staff-routing-rules/${editingRuleId}`, payload);
      setRoutingMessage('Routing rule updated successfully');
      cancelEditRule();
      fetchRoutingRules();
      fetchRoutingDefaults();
    } catch (error) {
      setRoutingMessage(error.response?.data?.error || 'Failed to update routing rule');
    } finally {
      setRoutingEditSubmitting(false);
    }
  };

  const formatApiError = (error) => {
    const raw = error?.response?.data?.error;
    if (raw == null) return 'Failed to save department escalation settings';
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw.message) return String(raw.message);
    try {
      return JSON.stringify(raw);
    } catch {
      return 'Failed to save department escalation settings';
    }
  };

  const saveRoutingDefaults = async (e) => {
    e.preventDefault();
    setDefaultsMessage('');
    const escalationMinutes = parseInt(routingDefaultsForm.escalation_minutes, 10);
    if (Number.isNaN(escalationMinutes) || escalationMinutes < 1) {
      setDefaultsMessage('Escalation minutes must be at least 1');
      return;
    }
    const followupRaw = String(routingDefaultsForm.guest_followup_ack_minutes ?? '').trim();
    const followupMinutes = followupRaw === '' ? null : parseInt(followupRaw, 10);
    if (followupRaw !== '' && (Number.isNaN(followupMinutes) || followupMinutes < 1)) {
      setDefaultsMessage('Guest follow-up acknowledgment minutes must be at least 1 (or leave blank to disable)');
      return;
    }

    try {
      setSavingDefaults(true);
      await api.put('/api/admin/staff-routing-defaults', {
        department: routingDefaultsForm.department,
        escalation_minutes: escalationMinutes,
        guest_followup_ack_minutes: followupMinutes,
        hod_staff_ids: routingDefaultsForm.hod_staff_ids || [],
        hod_staff_id: (routingDefaultsForm.hod_staff_ids || [])[0] || null
      });
      setDefaultsMessage('Department escalation settings saved successfully');
      fetchRoutingDefaults();
      fetchRoutingRules();
    } catch (error) {
      setDefaultsMessage(formatApiError(error));
    } finally {
      setSavingDefaults(false);
    }
  };

  const toggleRoutingRule = async (rule) => {
    try {
      await api.put(`/api/admin/staff-routing-rules/${rule.id}`, { is_active: !rule.is_active });
      fetchRoutingRules();
    } catch {
      setRoutingMessage('Failed to update routing rule status');
    }
  };

  const deleteRoutingRule = async (ruleId) => {
    if (!window.confirm('Delete this routing rule?')) return;
    try {
      await api.delete(`/api/admin/staff-routing-rules/${ruleId}`);
      fetchRoutingRules();
    } catch {
      setRoutingMessage('Failed to delete routing rule');
    }
  };

  return (
    <Layout>
      <div className="space-y-5 px-2 py-3 sm:px-3 sm:py-4 lg:px-3 lg:py-4 max-w-5xl mx-auto pb-8">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FiUsers className="h-5 w-5 text-gray-700" />
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Department Staff</h1>
          </div>
          <p className="text-sm text-gray-500">
            Manage employee and HOD contacts by department. Use the <strong>+ Add</strong> button on any department card for a faster workflow.
          </p>
        </div>

        {/* Section tabs — horizontal scroll on mobile */}
        <div className="-mx-1 overflow-x-auto pb-1">
          <div className="flex items-center gap-2 px-1 min-w-max">
          <button
            type="button"
            onClick={() => handleSectionChange('staff')}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors whitespace-nowrap ${
              activeSection === 'staff' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <FiUsers className="h-4 w-4" /> Staff Adding
          </button>
          <button
            type="button"
            onClick={() => handleSectionChange('routing')}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors whitespace-nowrap ${
              activeSection === 'routing' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <FiSliders className="h-4 w-4" /> Dispatch Routing
          </button>
          <button
            type="button"
            onClick={() => handleSectionChange('defaults')}
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition-colors whitespace-nowrap ${
              activeSection === 'defaults' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <FiSliders className="h-4 w-4" /> Dept Escalation
          </button>
          </div>
        </div>

        {activeSection === 'staff' && (
        <>
        {/* ── Add / Edit Form ────────────────────────────────────────── */}
        <div ref={formRef} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Form title bar */}
          <div className={`flex items-center justify-between px-5 py-3 ${selectedDeptColor.bg} border-b ${selectedDeptColor.ring} ring-1 ring-inset`}>
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${selectedDeptColor.dot}`} />
              <span className={`text-sm font-semibold ${selectedDeptColor.text}`}>
                Add Staff — {staffForm.department}
              </span>
            </div>
          </div>

          <form onSubmit={handleAddDepartmentStaff} className="p-5">
            {/* Row 1: Department selector */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Department</label>
              <div className="flex flex-wrap gap-2">
                {displayActivities.map((dept) => {
                  const c = DEPT_COLORS[dept] || DEFAULT_COLOR;
                  const selected = staffForm.department === dept;
                  return (
                    <button
                      key={dept}
                      type="button"
                      onClick={() => setStaffForm((p) => ({ ...p, department: dept }))}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-all border ${
                        selected
                          ? `${c.bg} ${c.text} border-transparent ring-2 ${c.ring}`
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {selected && <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />}
                      {dept}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Row 2: Name + Phone + Role */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Full Name</label>
                <input
                  type="text"
                  value={staffForm.name}
                  onChange={(e) => setStaffForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="Employee / HOD name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">WhatsApp Number</label>
                <input
                  type="text"
                  value={staffForm.phone}
                  onChange={(e) => setStaffForm((p) => ({ ...p, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="+91 XXXXXXXXXX"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Role</label>
                <div className="flex gap-2">
                  {['staff', 'hod'].map((role) => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setStaffForm((p) => ({ ...p, is_hod: role === 'hod' }))}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                        (role === 'hod') === staffForm.is_hod
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {role === 'hod' ? 'HOD' : 'Employee'}
                    </button>
                  ))}
                </div>
              </div>
            </div>


            {/* Hide / Test Account Checkbox */}
            <div className="pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!staffForm.is_hidden}
                  onChange={(e) => setStaffForm((p) => ({ ...p, is_hidden: e.target.checked }))}
                  className="rounded text-gray-900 focus:ring-gray-900 h-4 w-4 border-gray-300"
                />
                <span className="text-xs font-semibold text-gray-700">Hide from Dashboard &amp; Reports (Test Number)</span>
              </label>
              <p className="text-[11px] text-gray-400 mt-0.5 ml-6">
                Staff member will continue receiving dispatches &amp; WhatsApp alerts normally, but will be hidden from Dashboard Attendance, Top Staff, Dispatched &amp; Escalated lists, and Seen-by indicators.
              </p>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={staffSubmitting}
              className="w-full sm:w-auto px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-semibold disabled:opacity-60 transition-colors"
            >
              {staffSubmitting ? 'Saving...' : 'Add Contact'}
            </button>
          </form>
        </div>

        {/* ── Department Cards Grid ──────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {displayActivities.map((department) => {
            const staff = staffByDepartment[department] || [];
            const c = DEPT_COLORS[department] || DEFAULT_COLOR;
            const isCurrentDept = staffForm.department === department;

            return (
              <div
                key={department}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
                  isCurrentDept ? `ring-2 ${c.ring} border-transparent` : 'border-gray-200'
                }`}
              >
                {/* Card header */}
                <div className={`flex items-center justify-between px-4 py-3 ${c.bg} border-b border-gray-100`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`h-2 w-2 rounded-full flex-shrink-0 ${c.dot}`} />
                    <span className={`text-sm font-semibold truncate ${c.text}`}>{department}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-gray-400">{staff.length} contact{staff.length !== 1 ? 's' : ''}</span>
                    <button
                      type="button"
                      onClick={() => handleQuickAdd(department)}
                      title={`Add staff to ${department}`}
                      className="h-6 w-6 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-700 active:bg-gray-800 transition-colors"
                    >
                      <FiPlus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                {/* Staff list */}
                <div className="px-4 py-3">
                  {staffLoading ? (
                    <div className="flex gap-2">
                      {[1, 2].map((i) => (
                        <div key={i} className="h-10 flex-1 rounded-lg bg-gray-100 animate-pulse" />
                      ))}
                    </div>
                  ) : staff.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-4 gap-1">
                      <p className="text-xs text-gray-400">No contacts yet</p>
                      <button
                        type="button"
                        onClick={() => handleQuickAdd(department)}
                        className={`text-xs font-medium ${c.text} hover:underline`}
                      >
                        + Add first contact
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {staff.map((s) => (
                        <div
                          key={s.id}
                          className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 gap-2"
                        >
                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 truncate">{s.name}</span>
                              <span
                                className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                  s.is_hod ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                {s.is_hod ? 'HOD' : 'Staff'}
                              </span>
                              {s.is_hidden && (
                                <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                                  Hidden (Test)
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate">{s.phone || 'No phone'}</p>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleEditDepartmentStaff(s)}
                              className="h-7 w-7 flex items-center justify-center rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                              title="Edit"
                            >
                              <FiEdit2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteDepartmentStaff(s.id)}
                              className="h-7 w-7 flex items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Remove"
                            >
                              <FiTrash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
        )}

        {activeSection === 'defaults' && (
          <div className="space-y-4">
            <form onSubmit={saveRoutingDefaults} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Department Escalation Settings</p>
                {defaultsMessage && <span className="text-xs text-gray-600">{defaultsMessage}</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Service</label>
                  <select
                    value={routingDefaultsForm.department}
                    onChange={(e) => setRoutingDefaultsForm((prev) => ({ ...prev, department: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    {displayActivities.map((activity) => <option key={activity} value={activity}>{activity}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Escalation (min)</label>
                  <input
                    type="number"
                    min="1"
                    value={routingDefaultsForm.escalation_minutes}
                    onChange={(e) => setRoutingDefaultsForm((prev) => ({ ...prev, escalation_minutes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">HOD is notified if staff do not acknowledge within this time.</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Guest Follow-up Ack (min)</label>
                  <input
                    type="number"
                    min="1"
                    value={routingDefaultsForm.guest_followup_ack_minutes}
                    onChange={(e) => setRoutingDefaultsForm((prev) => ({ ...prev, guest_followup_ack_minutes: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="e.g. 3"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Blank disables it. After this delay, guest gets an auto “team is working on it” message.</p>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Default HODs (Escalation)</label>
                  <div className="border border-gray-300 rounded-lg p-2 max-h-32 overflow-y-auto space-y-1 bg-white">
                    {getHodOptions(routingDefaultsForm.department).length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No HODs found for this department</p>
                    ) : (
                      getHodOptions(routingDefaultsForm.department).map((staff) => {
                        const isSelected = Array.isArray(routingDefaultsForm.hod_staff_ids) && routingDefaultsForm.hod_staff_ids.includes(String(staff.id));
                        return (
                          <label key={staff.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => setRoutingDefaultsForm((prev) => {
                                const current = Array.isArray(prev.hod_staff_ids) ? prev.hod_staff_ids : [];
                                const id = String(staff.id);
                                return {
                                  ...prev,
                                  hod_staff_ids: current.includes(id)
                                    ? current.filter((x) => x !== id)
                                    : [...current, id]
                                };
                              })}
                              className="h-4 w-4"
                            />
                            <span>{staff.name} · {staff.phone}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Selected: {(routingDefaultsForm.hod_staff_ids || []).length}</p>
                </div>
              </div>
              <button
                type="submit"
                disabled={savingDefaults}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
              >
                {savingDefaults ? 'Saving...' : 'Save Department Settings'}
              </button>
            </form>
          </div>
        )}

        {activeSection === 'routing' && (
          <div className="space-y-4">
            {routingMessage && (
              <div className={`rounded-lg border px-3 py-2 text-sm ${routingMessage.includes('success') ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-700'}`}>{routingMessage}</div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Wizard header + step progress */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-900">Create Dispatch Routing Rule</p>
                  <span className="text-xs text-gray-400">Step {createStep} of 5</span>
                </div>
                {/* Progress dots */}
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${createStep === s ? 'bg-gray-900 text-white border-gray-900' : createStep > s ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-400 border-gray-200'}`}>
                        {createStep > s ? '✓' : s}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${createStep === s ? 'text-gray-900' : createStep > s ? 'text-green-600' : 'text-gray-400'}`}>
                        {s === 1 ? 'Department' : s === 2 ? 'Rooms' : s === 3 ? 'Employees' : s === 4 ? '1st HOD' : '2nd Super HOD'}
                      </span>
                      {s < 5 && <div className={`h-0.5 w-6 rounded transition-all ${createStep > s ? 'bg-green-400' : 'bg-gray-200'}`} />}
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={saveRoutingRule}>
                <div className="px-4 py-5">

                  {/* ── STEP 1: Department ── */}
                  {createStep === 1 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-3">Select the service department for this routing rule.</p>
                      <div className="flex flex-wrap gap-2">
                        {displayActivities.map((activity) => {
                          const c = DEPT_COLORS[activity] || DEFAULT_COLOR;
                          const selected = newRule.department === activity;
                          return (
                            <button key={activity} type="button"
                              onClick={() => setNewRule((prev) => ({ ...prev, department: activity, primary_phones: [], escalation_phones: [], super_hod_phones: [] }))}
                              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium border transition-all ${selected ? `${c.bg} ${c.text} border-transparent ring-2 ${c.ring} shadow-sm` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                            >
                              {selected && <span className={`h-2 w-2 rounded-full ${c.dot}`} />}
                              {activity}
                            </button>
                          );
                        })}
                      </div>
                      {newRule.department && (
                        <div className="mt-4 flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${(DEPT_COLORS[newRule.department] || DEFAULT_COLOR).dot}`} />
                          <span className="text-sm font-medium text-gray-700">Selected: <strong>{newRule.department}</strong></span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── STEP 2: Rooms ── */}
                  {createStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-xs text-gray-500">Choose a preset cluster or set a custom room range for <strong>{newRule.department}</strong>.</p>

                      {/* Presets */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Quick Presets</p>
                          <button type="button" onClick={() => setShowPresetManager(true)} className="text-[10px] text-gray-400 hover:text-gray-700 underline">Manage Presets</button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {roomPresets.map((preset) => {
                            const isActive = isPresetActive(newRule, preset);
                            return (
                              <button key={preset.label} type="button"
                                onClick={() => togglePreset(preset, newRule, setNewRule)}
                                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isActive ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-900 hover:text-white hover:border-gray-900'}`}
                              >
                                {preset.label}
                              </button>
                            );
                          })}
                          {roomPresets.length === 0 && <span className="text-xs text-gray-400 italic">No presets. Click Manage Presets to add wings/zones.</span>}
                        </div>
                      </div>

                      {/* Match type */}
                      <div>
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Or set manually</p>
                        <div className="flex gap-2 mb-3">
                          {['range', 'custom'].map((t) => (
                            <button key={t} type="button"
                              onClick={() => setNewRule((prev) => ({ ...prev, room_match_type: t }))}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${newRule.room_match_type === t ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                            >
                              {t === 'range' ? 'Range' : 'Custom List'}
                            </button>
                          ))}
                        </div>
                        {newRule.room_match_type === 'range' ? (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">From Room</label>
                              <input type="number" value={newRule.room_start} onChange={(e) => setNewRule((prev) => ({ ...prev, room_start: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 101" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">To Room</label>
                              <input type="number" value={newRule.room_end} onChange={(e) => setNewRule((prev) => ({ ...prev, room_end: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 120" />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Room Numbers (comma separated)</label>
                            <input type="text" value={newRule.room_custom_values} onChange={(e) => setNewRule((prev) => ({ ...prev, room_custom_values: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 101, 102, 120A" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── STEP 3: Employees ── */}
                  {createStep === 3 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-3">Select employees from <strong>{newRule.department}</strong> to assign to this rule.</p>
                      <div className="space-y-1.5 max-h-60 overflow-y-auto" onKeyDown={(e) => e.stopPropagation()}>
                        {getEmployeeOptions(newRule.department).map((staff) => {
                          const checked = (newRule.primary_phones || []).some((p) => normalizePhone(p) === normalizePhone(staff.phone));
                          return (
                            <button key={staff.id} type="button" onClick={(e) => { e.preventDefault(); togglePrimaryStaffSelection(staff.phone); }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-3 transition-all ${checked ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                              <span className={`h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center text-[11px] font-bold transition-all ${checked ? 'bg-white text-gray-900 border-white' : 'border-gray-300'}`}>{checked ? '✓' : ''}</span>
                              <div>
                                <span className="font-semibold">{staff.name}</span>
                                <span className={`ml-2 text-xs ${checked ? 'text-gray-300' : 'text-gray-400'}`}>{staff.phone}</span>
                              </div>
                            </button>
                          );
                        })}
                        {getEmployeeOptions(newRule.department).length === 0 && (
                          <p className="text-xs text-gray-500 italic">No active employees in this department. Add staff first.</p>
                        )}
                      </div>
                      {(newRule.primary_phones || []).length > 0 && (
                        <p className="mt-2 text-xs text-green-600 font-medium">{(newRule.primary_phones || []).length} employee(s) selected</p>
                      )}
                    </div>
                  )}

                  {/* ── STEP 4: 1st Tier HOD Escalation ── */}
                  {createStep === 4 && (
                    <div>
                      <div className="mb-3">
                        <p className="text-xs text-gray-500">
                          Select 1st Tier HOD(s) from <strong>{newRule.department}</strong> who should receive initial escalation alerts.
                        </p>
                      </div>
                      <div className="space-y-1.5 max-h-60 overflow-y-auto" onKeyDown={(e) => e.stopPropagation()}>
                        {getHodOptions(newRule.department).map((staff) => {
                          const checked = (newRule.escalation_phones || []).some((p) => normalizePhone(p) === normalizePhone(staff.phone));
                          return (
                            <button
                              key={staff.id}
                              type="button"
                              onClick={(e) => { e.preventDefault(); toggleEscalationStaffSelection(staff.phone); }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-3 transition-all ${
                                checked ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span className={`h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center text-[11px] font-bold transition-all ${
                                checked ? 'bg-white text-gray-900 border-white' : 'border-gray-300'
                              }`}>
                                {checked ? '✓' : ''}
                              </span>
                              <div className="flex-1">
                                <span className="font-semibold">{staff.name}</span>
                                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">HOD</span>
                                <span className={`ml-2 text-xs ${checked ? 'text-gray-300' : 'text-gray-400'}`}>{staff.phone}</span>
                              </div>
                            </button>
                          );
                        })}
                        {getHodOptions(newRule.department).length === 0 && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                            No active HODs added for <strong>{newRule.department}</strong> yet. You can add HOD contacts in the "Staff Adding" tab.
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {(newRule.escalation_phones || []).length > 0
                            ? `${(newRule.escalation_phones || []).length} HOD(s) assigned`
                            : 'Using department default HOD(s)'}
                        </span>
                        {getHodOptions(newRule.department).length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const allHodPhones = getHodOptions(newRule.department).map((s) => s.phone);
                              const isAllSelected = allHodPhones.length > 0 && allHodPhones.every((p) => (newRule.escalation_phones || []).map(normalizePhone).includes(normalizePhone(p)));
                              setNewRule((prev) => ({
                                ...prev,
                                escalation_phones: isAllSelected ? [] : allHodPhones
                              }));
                            }}
                            className="text-xs text-blue-600 font-semibold hover:underline"
                          >
                            {getHodOptions(newRule.department).every((p) => (newRule.escalation_phones || []).map(normalizePhone).includes(normalizePhone(p.phone)))
                              ? 'Clear Selection'
                              : 'Select All HODs'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── STEP 5: 2nd Tier Super HOD Escalation ── */}
                  {createStep === 5 && (
                    <div className="space-y-4">
                      <div className="p-3 rounded-lg border border-purple-200 bg-purple-50/60 text-xs text-purple-900 space-y-1">
                        <p className="font-semibold text-purple-900">🚨 Super HOD (2nd Tier) Escalation</p>
                        <p>If the request remains incomplete after HOD escalation, a 2nd alert will be sent to the Super HOD(s) selected below after the configured delay.</p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Super HOD Delay Timer (mins after HOD notified)</label>
                        <input
                          type="number"
                          min="0"
                          value={newRule.super_hod_escalation_minutes || 0}
                          onChange={(e) => setNewRule((prev) => ({ ...prev, super_hod_escalation_minutes: e.target.value }))}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm bg-purple-50"
                          placeholder="e.g. 7 (0 = disabled)"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Set to 0 to disable Super HOD escalation for this rule.</p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Select Super HOD(s) for <strong>{newRule.department}</strong></p>
                        <div className="space-y-1.5 max-h-52 overflow-y-auto" onKeyDown={(e) => e.stopPropagation()}>
                          {getHodOptions(newRule.department).map((staff) => {
                            const checked = (newRule.super_hod_phones || []).some((p) => normalizePhone(p) === normalizePhone(staff.phone));
                            return (
                              <button
                                key={staff.id}
                                type="button"
                                onClick={(e) => { e.preventDefault(); toggleSuperHodStaffSelection(staff.phone); }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-3 transition-all ${
                                  checked ? 'bg-purple-900 text-white border-purple-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-purple-50'
                                }`}
                              >
                                <span className={`h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center text-[11px] font-bold transition-all ${
                                  checked ? 'bg-white text-purple-900 border-white' : 'border-gray-300'
                                }`}>
                                  {checked ? '✓' : ''}
                                </span>
                                <div className="flex-1">
                                  <span className="font-semibold">{staff.name}</span>
                                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-200 text-purple-900">Super HOD</span>
                                  <span className={`ml-2 text-xs ${checked ? 'text-purple-200' : 'text-gray-400'}`}>{staff.phone}</span>
                                </div>
                              </button>
                            );
                          })}
                          {getHodOptions(newRule.department).length === 0 && (
                            <p className="text-xs text-gray-400 italic">No HODs available in this department.</p>
                          )}
                        </div>
                        {(newRule.super_hod_phones || []).length > 0 && (
                          <p className="mt-2 text-xs text-purple-700 font-semibold">{(newRule.super_hod_phones || []).length} Super HOD(s) assigned</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Wizard footer navigation */}
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                  <button type="button"
                    onClick={() => setCreateStep((s) => Math.max(1, s - 1))}
                    disabled={createStep === 1}
                    className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    ← Back
                  </button>
                  {createStep < 5 ? (
                    <button key="next-btn" type="button"
                      onClick={() => setCreateStep((s) => Math.min(5, s + 1))}
                      className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all"
                    >
                      Next →
                    </button>
                  ) : (
                    <button key="submit-btn" type="submit" className="px-5 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-all">
                      Create Rule ✓
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Dispatch Routing Rules</p>
                <span className="text-xs text-gray-400">{routingRules.length} rule{routingRules.length !== 1 ? 's' : ''}</span>
              </div>
              {loadingRouting ? (
                <div className="px-4 py-6 text-sm text-gray-500 text-center">Loading routing rules...</div>
              ) : routingRules.length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-400 text-center italic">No routing rules configured yet. Use the wizard above to create one.</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {routingRules.map((rule) => {
                    const c = DEPT_COLORS[rule.department] || DEFAULT_COLOR;
                    // Resolve primary phones → names
                    const primaryPhones = Array.isArray(rule.primary_phones) && rule.primary_phones.length > 0
                      ? rule.primary_phones : [rule.primary_phone].filter(Boolean);
                    const primaryNames = primaryPhones.map((ph) => {
                      const s = (departmentStaff || []).find((st) => normalizePhone(st.phone) === normalizePhone(ph));
                      return s ? s.name : ph;
                    });
                    // Resolve HOD phones → names
                    const deptDefaults = routingDefaultsMap[rule.department] || {};
                    const hasRuleHods = Array.isArray(rule.escalation_phones) && rule.escalation_phones.length > 0;
                    const hodPhones = hasRuleHods
                      ? rule.escalation_phones
                      : (Array.isArray(deptDefaults.escalation_phones) && deptDefaults.escalation_phones.length > 0
                        ? deptDefaults.escalation_phones
                        : (deptDefaults.escalation_phone ? [deptDefaults.escalation_phone] : (rule.escalation_phone ? [rule.escalation_phone] : [])));
                    const hodNames = hodPhones.map((ph) => {
                      const s = (departmentStaff || []).find((st) => normalizePhone(st.phone) === normalizePhone(ph));
                      return s ? s.name : ph;
                    });
                    const escalationMins = deptDefaults.escalation_minutes ?? rule.escalation_minutes;
                    const superHodPhones = Array.isArray(rule.super_hod_phones) && rule.super_hod_phones.length > 0
                      ? rule.super_hod_phones
                      : (rule.super_hod_phone ? [rule.super_hod_phone] : []);
                    const superHodNames = superHodPhones.map((ph) => {
                      const s = (departmentStaff || []).find((st) => normalizePhone(st.phone) === normalizePhone(ph));
                      return s ? s.name : ph;
                    });
                    return (
                      <div key={rule.id} className="px-4 py-4">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 w-full items-start">
                          {/* Department & Rooms */}
                          <div className="lg:col-span-3 flex flex-col gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.bg} ${c.text}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                                {rule.department}
                              </span>
                              {escalationMins != null && (
                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                  ⏱ {escalationMins}m
                                </span>
                              )}
                            </div>
                            <div className="inline-flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium bg-gray-50 text-gray-700 border border-gray-100">
                              <span>🚪</span>
                              <span className="leading-snug break-words">{getRoomMatchLabel(rule)}</span>
                            </div>
                          </div>

                          {/* Assigned Employees */}
                          <div className="lg:col-span-3 flex flex-col items-start lg:border-l lg:border-gray-100 lg:pl-4">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Assigned To</span>
                            {primaryNames.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {primaryNames.map((name, i) => (
                                  <span key={i} className="inline-block rounded border border-gray-200 bg-white text-gray-700 shadow-sm text-[11px] font-medium px-2 py-1">{name}</span>
                                ))}
                              </div>
                            ) : <span className="text-[11px] text-gray-400 italic">None assigned</span>}
                          </div>

                          {/* Escalates To (1st HOD) */}
                          <div className="lg:col-span-2 flex flex-col items-start lg:border-l lg:border-gray-100 lg:pl-4">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">1st HOD</span>
                              {hasRuleHods ? (
                                <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Rule</span>
                              ) : (
                                <span className="text-[9px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Default</span>
                              )}
                            </div>
                            {hodNames.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {hodNames.map((name, i) => (
                                  <span key={i} className={`inline-block rounded border text-[11px] font-medium px-2 py-1 ${hasRuleHods ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-100 bg-red-50 text-red-700'}`}>{name}</span>
                                ))}
                              </div>
                            ) : <span className="text-[11px] text-gray-400 italic">None</span>}
                          </div>

                          {/* 2nd Tier Super HOD */}
                          <div className="lg:col-span-2 flex flex-col items-start lg:border-l lg:border-gray-100 lg:pl-4">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="text-[10px] font-bold text-purple-700 uppercase tracking-wider">2nd Super HOD</span>
                              {rule.super_hod_escalation_minutes > 0 && (
                                <span className="text-[9px] font-bold text-purple-700 bg-purple-50 border border-purple-200 px-1.5 py-0.5 rounded">+{rule.super_hod_escalation_minutes}m</span>
                              )}
                            </div>
                            {superHodNames.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {superHodNames.map((name, i) => (
                                  <span key={i} className="inline-block rounded border border-purple-200 bg-purple-50 text-purple-900 text-[11px] font-medium px-2 py-1">{name}</span>
                                ))}
                              </div>
                            ) : <span className="text-[11px] text-gray-400 italic">Disabled</span>}
                          </div>

                          {/* Actions */}
                          <div className="lg:col-span-2 flex flex-row lg:flex-col items-center lg:items-end justify-start lg:justify-start gap-2 lg:border-l lg:border-gray-100 lg:pl-4 mt-2 lg:mt-0">
                            <div className="flex gap-2 w-full">
                              <button type="button" onClick={() => startEditRule(rule)}
                                className="flex-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all">
                                Edit
                              </button>
                              <button type="button" onClick={() => deleteRoutingRule(rule.id)}
                                className="flex-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-all">
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {editingStaffId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={closeStaffEditModal} />
            <form onSubmit={handleUpdateDepartmentStaff} className="relative z-10 w-full max-w-2xl bg-white rounded-xl shadow-xl border border-gray-200 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Edit Staff Contact</h3>
                <button type="button" onClick={closeStaffEditModal} className="text-gray-500 hover:text-gray-700">
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Department</label>
                  <select
                    value={staffEditForm.department}
                    onChange={(e) => setStaffEditForm((p) => ({ ...p, department: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    {displayActivities.map((activity) => <option key={activity} value={activity}>{activity}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Role</label>
                  <div className="flex gap-2">
                    {['staff', 'hod'].map((role) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => setStaffEditForm((p) => ({ ...p, is_hod: role === 'hod' }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-all ${
                          (role === 'hod') === staffEditForm.is_hod
                            ? 'bg-gray-900 text-white border-gray-900'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {role === 'hod' ? 'HOD' : 'Employee'}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name</label>
                  <input
                    type="text"
                    value={staffEditForm.name}
                    onChange={(e) => setStaffEditForm((p) => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">WhatsApp Number</label>
                  <input
                    type="text"
                    value={staffEditForm.phone}
                    onChange={(e) => setStaffEditForm((p) => ({ ...p, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    required
                  />
                </div>
                <div className="pt-1">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!staffEditForm.is_hidden}
                      onChange={(e) => setStaffEditForm((p) => ({ ...p, is_hidden: e.target.checked }))}
                      className="rounded text-gray-900 focus:ring-gray-900 h-4 w-4 border-gray-300"
                    />
                    <span className="text-xs font-semibold text-gray-700">Hide from Dashboard &amp; Reports (Test Number)</span>
                  </label>
                  <p className="text-[11px] text-gray-400 mt-0.5 ml-6">
                    Staff member will continue receiving dispatches &amp; WhatsApp alerts normally, but will be hidden from Dashboard Attendance, Top Staff, Dispatched &amp; Escalated lists, and Seen-by indicators.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeStaffEditModal} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={staffEditSubmitting} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-60">
                  {staffEditSubmitting ? 'Updating...' : 'Update Contact'}
                </button>
              </div>
            </form>
          </div>
        )}

        {editingRuleId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={cancelEditRule} />
            <div className="relative z-10 w-full max-w-xl bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col">

              {/* Modal header + step progress */}
              <div className="px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-gray-900">Edit Dispatch Routing Rule</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">Step {editStep} of 5</span>
                    <button type="button" onClick={cancelEditRule} className="text-gray-400 hover:text-gray-700">
                      <FiX className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {[1,2,3,4,5].map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${editStep === s ? 'bg-gray-900 text-white border-gray-900' : editStep > s ? 'bg-green-500 text-white border-green-500' : 'bg-white text-gray-400 border-gray-200'}`}>
                        {editStep > s ? '✓' : s}
                      </div>
                      <span className={`text-xs font-medium hidden sm:block ${editStep === s ? 'text-gray-900' : editStep > s ? 'text-green-600' : 'text-gray-400'}`}>
                        {s === 1 ? 'Department' : s === 2 ? 'Rooms' : s === 3 ? 'Employees' : s === 4 ? '1st HOD' : '2nd Super HOD'}
                      </span>
                      {s < 5 && <div className={`h-0.5 w-6 rounded transition-all ${editStep > s ? 'bg-green-400' : 'bg-gray-200'}`} />}
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={handleUpdateRoutingRule} className="flex flex-col flex-1 overflow-hidden">
                <div className="px-5 py-5 overflow-y-auto flex-1">

                  {/* STEP 1: Department */}
                  {editStep === 1 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-3">Select the service department for this routing rule.</p>
                      <div className="flex flex-wrap gap-2">
                        {displayActivities.map((activity) => {
                          const c = DEPT_COLORS[activity] || DEFAULT_COLOR;
                          const selected = editRuleForm.department === activity;
                          return (
                            <button key={activity} type="button"
                              onClick={() => setEditRuleForm((prev) => ({ ...prev, department: activity, primary_phones: [], escalation_phones: [] }))}
                              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium border transition-all ${selected ? `${c.bg} ${c.text} border-transparent ring-2 ${c.ring} shadow-sm` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                            >
                              {selected && <span className={`h-2 w-2 rounded-full ${c.dot}`} />}
                              {activity}
                            </button>
                          );
                        })}
                      </div>
                      {editRuleForm.department && (
                        <div className="mt-4 flex items-center gap-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${(DEPT_COLORS[editRuleForm.department] || DEFAULT_COLOR).dot}`} />
                          <span className="text-sm font-medium text-gray-700">Selected: <strong>{editRuleForm.department}</strong></span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* STEP 2: Rooms */}
                  {editStep === 2 && (
                    <div className="space-y-4">
                      <p className="text-xs text-gray-500">Choose a preset or set a room range for <strong>{editRuleForm.department}</strong>.</p>
                      <div>
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Quick Presets</p>
                        <div className="flex flex-wrap gap-2">
                          {roomPresets.map((preset) => {
                            const isActive = isPresetActive(editRuleForm, preset);
                            return (
                              <button key={preset.label} type="button"
                                onClick={() => togglePreset(preset, editRuleForm, setEditRuleForm)}
                                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${isActive ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-900 hover:text-white hover:border-gray-900'}`}
                              >
                                {preset.label}
                              </button>
                            );
                          })}
                          {roomPresets.length === 0 && <span className="text-xs text-gray-400 italic">No presets configured.</span>}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Or set manually</p>
                        <div className="flex gap-2 mb-3">
                          {['range', 'custom'].map((t) => (
                            <button key={t} type="button"
                              onClick={() => setEditRuleForm((prev) => ({ ...prev, room_match_type: t }))}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${editRuleForm.room_match_type === t ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                            >
                              {t === 'range' ? 'Range' : 'Custom List'}
                            </button>
                          ))}
                        </div>
                        {editRuleForm.room_match_type === 'range' ? (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">From Room</label>
                              <input type="number" value={editRuleForm.room_start} onChange={(e) => setEditRuleForm((prev) => ({ ...prev, room_start: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 101" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">To Room</label>
                              <input type="number" value={editRuleForm.room_end} onChange={(e) => setEditRuleForm((prev) => ({ ...prev, room_end: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 120" />
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Room Numbers (comma separated)</label>
                            <input type="text" value={editRuleForm.room_custom_values} onChange={(e) => setEditRuleForm((prev) => ({ ...prev, room_custom_values: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 101, 102, 120A" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Employees */}
                  {editStep === 3 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-3">Select employees from <strong>{editRuleForm.department}</strong> to assign to this rule.</p>
                      {/* stopPropagation prevents keyboard events (Enter) inside list from bubbling to form and triggering submit */}
                      <div className="space-y-1.5 max-h-60 overflow-y-auto" onKeyDown={(e) => e.stopPropagation()}>
                        {getEmployeeOptions(editRuleForm.department).map((staff) => {
                          const checked = (editRuleForm.primary_phones || []).some((p) => normalizePhone(p) === normalizePhone(staff.phone));
                          return (
                            <button key={staff.id} type="button" onClick={(e) => { e.preventDefault(); toggleEditPrimaryStaffSelection(staff.phone); }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-3 transition-all ${checked ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                              <span className={`h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center text-[11px] font-bold transition-all ${checked ? 'bg-white text-gray-900 border-white' : 'border-gray-300'}`}>{checked ? '✓' : ''}</span>
                              <div>
                                <span className="font-semibold">{staff.name}</span>
                                <span className={`ml-2 text-xs ${checked ? 'text-gray-300' : 'text-gray-400'}`}>{staff.phone}</span>
                              </div>
                            </button>
                          );
                        })}
                        {getEmployeeOptions(editRuleForm.department).length === 0 && (
                          <p className="text-xs text-gray-500">No active employees in this activity.</p>
                        )}
                      </div>
                      {(editRuleForm.primary_phones || []).length > 0 && (
                        <p className="mt-2 text-xs text-green-600 font-medium">{(editRuleForm.primary_phones || []).length} employee(s) selected</p>
                      )}
                    </div>
                  )}

                  {/* STEP 4: 1st Tier HOD Escalation */}
                  {editStep === 4 && (
                    <div>
                      <div className="mb-3">
                        <p className="text-xs text-gray-500">
                          Select 1st Tier HOD(s) from <strong>{editRuleForm.department}</strong> who should receive initial escalation alerts for these rooms.
                        </p>
                      </div>
                      <div className="space-y-1.5 max-h-60 overflow-y-auto" onKeyDown={(e) => e.stopPropagation()}>
                        {getHodOptions(editRuleForm.department).map((staff) => {
                          const checked = (editRuleForm.escalation_phones || []).some((p) => normalizePhone(p) === normalizePhone(staff.phone));
                          return (
                            <button
                              key={staff.id}
                              type="button"
                              onClick={(e) => { e.preventDefault(); toggleEditEscalationStaffSelection(staff.phone); }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-3 transition-all ${
                                checked ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span className={`h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center text-[11px] font-bold transition-all ${
                                checked ? 'bg-white text-gray-900 border-white' : 'border-gray-300'
                              }`}>
                                {checked ? '✓' : ''}
                              </span>
                              <div className="flex-1">
                                <span className="font-semibold">{staff.name}</span>
                                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">HOD</span>
                                <span className={`ml-2 text-xs ${checked ? 'text-gray-300' : 'text-gray-400'}`}>{staff.phone}</span>
                              </div>
                            </button>
                          );
                        })}
                        {getHodOptions(editRuleForm.department).length === 0 && (
                          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                            No active HODs added for <strong>{editRuleForm.department}</strong> yet. You can add HOD contacts in the "Staff Adding" tab.
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {(editRuleForm.escalation_phones || []).length > 0
                            ? `${(editRuleForm.escalation_phones || []).length} HOD(s) specifically assigned`
                            : 'Using department default HOD(s)'}
                        </span>
                        {getHodOptions(editRuleForm.department).length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              const allHodPhones = getHodOptions(editRuleForm.department).map((s) => s.phone);
                              const isAllSelected = allHodPhones.length > 0 && allHodPhones.every((p) => (editRuleForm.escalation_phones || []).map(normalizePhone).includes(normalizePhone(p)));
                              setEditRuleForm((prev) => ({
                                ...prev,
                                escalation_phones: isAllSelected ? [] : allHodPhones
                              }));
                            }}
                            className="text-xs text-blue-600 font-semibold hover:underline"
                          >
                            {getHodOptions(editRuleForm.department).every((p) => (editRuleForm.escalation_phones || []).map(normalizePhone).includes(normalizePhone(p.phone)))
                              ? 'Clear Selection'
                              : 'Select All HODs'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 5: 2nd Tier Super HOD Escalation */}
                  {editStep === 5 && (
                    <div className="space-y-4">
                      <div className="p-3 rounded-lg border border-purple-200 bg-purple-50/60 text-xs text-purple-900 space-y-1">
                        <p className="font-semibold text-purple-900">🚨 Super HOD (2nd Tier) Escalation</p>
                        <p>If the request remains incomplete after HOD escalation, a 2nd alert will be sent to the Super HOD(s) selected below after the configured delay.</p>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Super HOD Delay Timer (mins after HOD notified)</label>
                        <input
                          type="number"
                          min="0"
                          value={editRuleForm.super_hod_escalation_minutes || 0}
                          onChange={(e) => setEditRuleForm((prev) => ({ ...prev, super_hod_escalation_minutes: e.target.value }))}
                          className="w-full px-3 py-2 border border-purple-300 rounded-lg text-sm bg-purple-50"
                          placeholder="e.g. 7 (0 = disabled)"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Set to 0 to disable Super HOD escalation for this rule.</p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Select Super HOD(s) for <strong>{editRuleForm.department}</strong></p>
                        <div className="space-y-1.5 max-h-52 overflow-y-auto" onKeyDown={(e) => e.stopPropagation()}>
                          {getHodOptions(editRuleForm.department).map((staff) => {
                            const checked = (editRuleForm.super_hod_phones || []).some((p) => normalizePhone(p) === normalizePhone(staff.phone));
                            return (
                              <button
                                key={staff.id}
                                type="button"
                                onClick={(e) => { e.preventDefault(); toggleEditSuperHodStaffSelection(staff.phone); }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm flex items-center gap-3 transition-all ${
                                  checked ? 'bg-purple-900 text-white border-purple-900' : 'bg-white border-gray-200 text-gray-700 hover:bg-purple-50'
                                }`}
                              >
                                <span className={`h-5 w-5 rounded border-2 flex-shrink-0 flex items-center justify-center text-[11px] font-bold transition-all ${
                                  checked ? 'bg-white text-purple-900 border-white' : 'border-gray-300'
                                }`}>
                                  {checked ? '✓' : ''}
                                </span>
                                <div className="flex-1">
                                  <span className="font-semibold">{staff.name}</span>
                                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-200 text-purple-900">Super HOD</span>
                                  <span className={`ml-2 text-xs ${checked ? 'text-purple-200' : 'text-gray-400'}`}>{staff.phone}</span>
                                </div>
                              </button>
                            );
                          })}
                          {getHodOptions(editRuleForm.department).length === 0 && (
                            <p className="text-xs text-gray-400 italic">No HODs available in this department.</p>
                          )}
                        </div>
                        {(editRuleForm.super_hod_phones || []).length > 0 && (
                          <p className="mt-2 text-xs text-purple-700 font-semibold">{(editRuleForm.super_hod_phones || []).length} Super HOD(s) assigned</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Wizard footer */}
                <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50 flex-shrink-0">
                  <button type="button"
                    onClick={() => editStep === 1 ? cancelEditRule() : setEditStep((s) => Math.max(1, s - 1))}
                    className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 transition-all"
                  >
                    {editStep === 1 ? 'Cancel' : '← Back'}
                  </button>
                  {editStep < 5 ? (
                    <button key="edit-next-btn" type="button"
                      onClick={() => setEditStep((s) => Math.min(5, s + 1))}
                      className="px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-all"
                    >
                      Next →
                    </button>
                  ) : (
                    <button key="edit-submit-btn" type="submit" disabled={routingEditSubmitting} className="px-5 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-60 transition-all">
                      {routingEditSubmitting ? 'Updating...' : 'Update Rule ✓'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}



        {/* ── Preset Manager Modal ── */}
        {showPresetManager && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowPresetManager(false)} />
            <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900">Manage Room Presets</h3>
                <button type="button" onClick={() => setShowPresetManager(false)} className="text-gray-500 hover:text-gray-700">
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500">Create named room clusters (wings, floors, zones) to quickly fill room ranges when building dispatch rules.</p>

              {/* Existing presets list */}
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {roomPresets.length === 0 && <p className="text-xs text-gray-400 italic">No presets yet.</p>}
                {roomPresets.map((preset, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                    <div>
                      <span className="text-sm font-semibold text-gray-900">{preset.label}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        {preset.type === 'custom' ? preset.custom : `Rooms ${preset.start} – ${preset.end}`}
                      </span>
                    </div>
                    <button type="button" onClick={() => removePreset(idx)} className="h-7 w-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                      <FiTrash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add new preset form */}
              <div className="border-t border-gray-100 pt-3 space-y-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Add New Preset</p>
                <input
                  type="text"
                  placeholder="Preset name (e.g. A Wing, Ground Floor)"
                  value={newPreset.label}
                  onChange={(e) => setNewPreset((p) => ({ ...p, label: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <div className="flex gap-2">
                  {['range', 'custom'].map((t) => (
                    <button key={t} type="button"
                      onClick={() => setNewPreset((p) => ({ ...p, type: t }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${newPreset.type === t ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                    >
                      {t === 'range' ? 'Range' : 'Custom List'}
                    </button>
                  ))}
                </div>
                {newPreset.type === 'range' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" placeholder="From room" value={newPreset.start} onChange={(e) => setNewPreset((p) => ({ ...p, start: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    <input type="number" placeholder="To room" value={newPreset.end} onChange={(e) => setNewPreset((p) => ({ ...p, end: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                ) : (
                  <input type="text" placeholder="e.g. 101, 102, 120A" value={newPreset.custom} onChange={(e) => setNewPreset((p) => ({ ...p, custom: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                )}
                <button type="button" onClick={addPreset} className="w-full py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 transition-colors">
                  <FiPlus className="inline h-3.5 w-3.5 mr-1" /> Add Preset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default DepartmentStaff;
